import React, { useMemo } from "react";
import { Box, Flex, type LayoutProps, SimpleGrid, type SimpleGridProps, type SpaceProps } from "@chakra-ui/react";
import { FillImage } from "components/images";
import { type Theme } from "contexts/color-theme";
import { type ImageProps } from "next/image";
import { ProgressTabs, type ProgressTabProps } from "ui/tabs";
import { TextColumn, type TextColumnProps } from "ui/text";
import { desktopOnlyDisplay, mobileOnlyDisplay } from "utils";

export type ProductFeature = {
  /** Unique identifier required for key mapping, since the description is optional and the title is a `ReactNode`.  */
  id: string;

  /**
   * The static image to display for mobile views.
   * Will be displayed as a covered/centered image.
   */
  mobileImage: ImageProps;
} & Pick<TextColumnProps, "title" | "description" | "cta">;

export type ProductFeaturesProps = {
  /**
   * Used to toggle the `priority` setting for the static images used in mobile views.
   */
  isPriority?: boolean;

  /**
   * The theme determines the text colors used in the component.
   */
  theme: Theme;

  /**
   * The media to display in desktop views, either an image or lottie animation.
   */
  media: JSX.Element;

  /**
   * The product features that are shown below the animation for desktop views and with
   * their corresponding images in a slideshow for mobile views.
   */
  productFeatures: ProductFeature[];

  /**
   * Additional props to pass to the `SimpleGrid` that contains the `TextColumn` features in desktop view.
   */
  featureGridProps?: Omit<SimpleGridProps, "columns">;

  /**
   * Minimum height to set on the product feature `TextColumn`s in the mobile view slideshow.
   * Used to help prevent unexpected layout shifting when there are different lengths of copy on different tabs in the slideshow.
   */
  mobileFeatureTextMinH?: LayoutProps["minH"];
} & SpaceProps;

/**
 * Displays a media element and all product features for desktop views,
 * and a slideshow of individual product features with static images on mobile views.
 */
export function ProductFeatures({
  isPriority,
  theme,
  media,
  productFeatures,
  featureGridProps,
  mobileFeatureTextMinH,
  ...spaceProps
}: ProductFeaturesProps) {
  const featureTabs: ProgressTabProps[] = useMemo(() => {
    return productFeatures.map((f) => ({
      id: f.id,
      title: f.title,
      description: f.description,
      cta: f.cta,
      media: (
        <FillImage {...f.mobileImage} style={{ objectFit: "cover", objectPosition: "center" }} priority={isPriority} />
      ),
    }));
  }, [productFeatures, isPriority]);

  return (
    <Box {...spaceProps}>
      <Flex display={desktopOnlyDisplay("flex")} flexDir="column">
        {media}

        <SimpleGrid columns={3} gap="bigger" mt="bigger" {...featureGridProps}>
          {productFeatures.map((feature) => (
            <TextColumn
              key={feature.id}
              theme={theme}
              title={feature.title}
              description={feature.description}
              cta={feature.cta}
            />
          ))}
        </SimpleGrid>
      </Flex>

      <ProgressTabs display={mobileOnlyDisplay()} theme={theme} tabs={featureTabs} textMinH={mobileFeatureTextMinH} />
    </Box>
  );
}
